import { useContext, useRef } from "react";
import { type V2Bag, type V2Value } from "@/features/applications/api/applications.api";
import { Button } from "@/components/ui/button";
import { type FieldDef } from "../model";
import { ValidationContext } from "../validation";
import { useReadOnly } from "../readonly";
import { SectionCard } from "../primitives";
import { FieldGrid } from "../FieldGrid";
import { zipAutoFill } from "../zipAutoFill";

export function RepeatEditor({
  title,
  noun,
  collection,
  rowFields,
  rows,
  onChange,
  onSave,
}: {
  title: string;
  noun?: string;
  collection: string;
  rowFields: FieldDef[];
  rows: V2Bag[];
  onChange: (rows: V2Bag[] | ((prev: V2Bag[]) => V2Bag[])) => void;
  onSave?: () => void;
}) {
  const word = (noun ?? title).toLowerCase();
  const ctx = useContext(ValidationContext);
  const readOnly = useReadOnly();
  const minKey = `${collection}.__min__`;
  const minMissing = !readOnly && ctx.missing.has(minKey);
  const rowIds = useRef<number[]>([]);
  const nextRowId = useRef(0);
  while (rowIds.current.length < rows.length) rowIds.current.push(nextRowId.current++);
  if (rowIds.current.length > rows.length) rowIds.current.length = rows.length;
  function patchRow(index: number, patch: V2Bag) {
    onChange((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function update(index: number, key: string, value: V2Value) {
    patchRow(index, { [key]: value });
    zipAutoFill(key, value, (patch) => {
      patchRow(index, patch);
      for (const k of Object.keys(patch)) ctx.markFilled(`${collection}.${index}.${k}`);
    });
  }
  return (
    <div className="space-y-4">
      {rows.length === 0 && (
        minMissing
          ? <p data-missing="true" className="text-sm text-destructive">At least one {word} is required.</p>
          : <p className="text-sm text-slate-500">None added yet.</p>
      )}
      {rows.map((row, i) => {
        const suffix = typeof row.purpose === "string" && row.purpose ? ` — ${row.purpose}` : "";
        return (
          <SectionCard key={rowIds.current[i]} title={`${title} ${i + 1}${suffix}`}>
            <ValidationContext.Provider value={{ missing: ctx.missing, errors: ctx.errors, markFilled: ctx.markFilled, markMissing: ctx.markMissing, markError: ctx.markError, prefix: `${collection}.${i}.` }}>
              <FieldGrid fields={rowFields} bag={row} onChange={(k, v) => update(i, k, v)} />
            </ValidationContext.Provider>
            {!readOnly && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { rowIds.current.splice(i, 1); onChange((prev) => prev.filter((_, idx) => idx !== i)); }}
                  className="text-destructive hover:text-destructive"
                >
                  Remove {word}
                </Button>
                {onSave && (
                  <Button type="button" onClick={onSave}>
                    Save {word}
                  </Button>
                )}
              </div>
            )}
          </SectionCard>
        );
      })}
      {!readOnly && (
        <div className="flex justify-center">
          <Button type="button" onClick={() => { onChange((prev) => [...prev, {}]); ctx.markFilled(minKey); }}>
            + Add {word}
          </Button>
        </div>
      )}
    </div>
  );
}
